import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import API, { fmt } from '@/lib/api';
import { MagnifyingGlass, ArrowSquareOut, Warning, Tag, Folder, CurrencyEur } from '@phosphor-icons/react';

const TYPE_COLORS = {
  ART: 'bg-blue-50 text-blue-700 border-blue-200',
  CAT: 'bg-purple-50 text-purple-700 border-purple-200',
  PG: 'bg-red-50 text-red-700 border-red-200',
  ALLES: 'bg-green-50 text-green-700 border-green-200',
};

export default function KortingenAuditPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], totaal_klanten: 0, totaal_regels: 0, totaal_korting_eur: 0 });
  const [q, setQ] = useState('');
  const [typeFilter, setTypeFilter] = useState('ALLE');
  const [sortBy, setSortBy] = useState('totaal_korting_eur');
  const [sortDir, setSortDir] = useState('desc');

  useEffect(() => {
    setLoading(true);
    API.get('/kortingen-audit')
      .then(r => setData(r.data || { items: [] }))
      .catch(() => setData({ items: [], totaal_klanten: 0, totaal_regels: 0, totaal_korting_eur: 0 }))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    let rows = (data.items || []).filter(it => {
      if (typeFilter !== 'ALLE' && it.korting_type !== typeFilter) return false;
      if (!ql) return true;
      return [it.klant_naam, it.klant_nr, it.korting_label]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(ql));
    });
    rows = [...rows].sort((a, b) => {
      const va = a[sortBy] ?? '';
      const vb = b[sortBy] ?? '';
      const cmp = (typeof va === 'number' && typeof vb === 'number')
        ? va - vb
        : String(va).localeCompare(String(vb));
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [data, q, typeFilter, sortBy, sortDir]);

  const totaalGefilterd = useMemo(
    () => filtered.reduce((s, it) => s + (Number(it.totaal_korting_eur) || 0), 0),
    [filtered]
  );

  const sortClick = (key) => {
    if (sortBy === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(key); setSortDir('desc'); }
  };
  const sortIcon = (key) => sortBy === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  // Identificeer "vergeten" of "verdachte" kortingen
  const vergeten = filtered.filter(it => it.aantal_documenten === 0 && it.percentage > 0);
  const grootste = filtered.filter(it => it.aantal_documenten > 0).sort((a, b) => b.totaal_korting_eur - a.totaal_korting_eur).slice(0, 1)[0];

  return (
    <div className="p-6 max-w-7xl" data-testid="kortingen-audit-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold">Kortingen audit</h1>
          <p className="text-xs text-gray-500 mt-1">
            Overzicht van klantspecifieke kortingen en hun werkelijke gebruik op documenten.
            Detecteert vergeten kortingen, misbruik of inactieve rules.
          </p>
        </div>
      </div>

      {/* KPI tiles */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="p-3 border-l-4 bg-gray-50 border-gray-400" data-testid="kpi-klanten">
          <div className="text-[10px] uppercase text-gray-600 font-bold">Klanten met kortingen</div>
          <div className="text-lg font-bold font-mono mt-1">{data.totaal_klanten}</div>
        </div>
        <div className="p-3 border-l-4 bg-blue-50 border-blue-500" data-testid="kpi-regels">
          <div className="text-[10px] uppercase text-gray-600 font-bold">Actieve korting-regels</div>
          <div className="text-lg font-bold font-mono mt-1">{data.totaal_regels}</div>
        </div>
        <div className="p-3 border-l-4 bg-green-50 border-green-500" data-testid="kpi-totaal-eur">
          <div className="text-[10px] uppercase text-gray-600 font-bold">Totaal korting gegeven</div>
          <div className="text-lg font-bold font-mono mt-1">{fmt(data.totaal_korting_eur)}</div>
        </div>
        <div className={`p-3 border-l-4 ${vergeten.length > 0 ? 'bg-amber-50 border-amber-500' : 'bg-gray-50 border-gray-300'}`} data-testid="kpi-vergeten">
          <div className="text-[10px] uppercase text-gray-600 font-bold">Vergeten regels (0 docs)</div>
          <div className={`text-lg font-bold font-mono mt-1 ${vergeten.length > 0 ? 'text-amber-700' : 'text-gray-500'}`}>
            {vergeten.length}
          </div>
        </div>
      </div>

      {/* Insights */}
      {grootste && (
        <div className="p-3 mb-4 bg-blue-50 border border-blue-200 text-xs flex items-start gap-2" data-testid="insight-grootste">
          <CurrencyEur size={16} className="text-blue-600 mt-0.5 shrink-0" />
          <div>
            <strong>Grootste effect:</strong> {grootste.klant_naam} — {grootste.korting_type} {grootste.korting_label} {grootste.percentage}%
            heeft <strong>{fmt(grootste.totaal_korting_eur)}</strong> aan korting gegeven over {grootste.aantal_documenten} document(en).
          </div>
        </div>
      )}
      {vergeten.length > 0 && (
        <div className="p-3 mb-4 bg-amber-50 border border-amber-200 text-xs flex items-start gap-2" data-testid="insight-vergeten">
          <Warning size={16} className="text-amber-600 mt-0.5 shrink-0" />
          <div>
            <strong>{vergeten.length}</strong> korting-regel(s) zijn nog nooit toegepast — mogelijk vergeten of niet meer relevant. Filter op "0 documenten" om te bekijken.
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-3 items-center">
        <div className="relative flex-1 min-w-[280px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="field-input pl-7 text-xs w-full"
            placeholder="Zoek op klant of korting-label..."
            value={q}
            onChange={e => setQ(e.target.value)}
            data-testid="audit-search"
          />
        </div>
        <select
          className="field-input text-xs"
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          data-testid="audit-type-filter"
        >
          <option value="ALLE">Alle types</option>
          <option value="ART">ART · Specifiek artikel</option>
          <option value="CAT">CAT · Categorie</option>
          <option value="PG">PG · Prijsgroep</option>
          <option value="ALLES">Alle artikels</option>
        </select>
        <div className="text-xs text-gray-500" data-testid="audit-totaal-gefilterd">
          {filtered.length} regel(s) · totaal: <strong>{fmt(totaalGefilterd)}</strong>
        </div>
      </div>

      {/* Tabel */}
      <div className="border border-gray-200 bg-white overflow-x-auto">
        <table className="data-table w-full">
          <thead>
            <tr>
              <th className="cursor-pointer hover:bg-gray-100" onClick={() => sortClick('klant_naam')}>Klant{sortIcon('klant_naam')}</th>
              <th>Type</th>
              <th className="cursor-pointer hover:bg-gray-100" onClick={() => sortClick('korting_label')}>Korting-doel{sortIcon('korting_label')}</th>
              <th className="right cursor-pointer hover:bg-gray-100" onClick={() => sortClick('percentage')}>%{sortIcon('percentage')}</th>
              <th className="right cursor-pointer hover:bg-gray-100" onClick={() => sortClick('aantal_documenten')}>Documenten{sortIcon('aantal_documenten')}</th>
              <th className="right cursor-pointer hover:bg-gray-100" onClick={() => sortClick('totaal_korting_eur')}>Totaal €{sortIcon('totaal_korting_eur')}</th>
              <th className="cursor-pointer hover:bg-gray-100" onClick={() => sortClick('laatst_gebruikt')}>Laatst gebruikt{sortIcon('laatst_gebruikt')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="text-center text-gray-400 py-8">Laden...</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={8} className="text-center text-gray-400 py-8">
                {data.totaal_regels === 0
                  ? 'Geen klantspecifieke kortingen actief.'
                  : 'Geen resultaten — pas de filters aan.'}
              </td></tr>
            )}
            {!loading && filtered.map((it, idx) => {
              const cssVergeten = it.aantal_documenten === 0 && it.percentage > 0;
              return (
                <tr key={`${it.klant_id}-${idx}`} className={cssVergeten ? 'bg-amber-50/40' : ''} data-testid={`audit-row-${idx}`}>
                  <td>
                    <div className="font-medium text-xs">{it.klant_naam}</div>
                    {it.klant_nr && <div className="text-[10px] text-gray-400 font-mono">{it.klant_nr}</div>}
                  </td>
                  <td>
                    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold border ${TYPE_COLORS[it.korting_type] || ''}`}>
                      {it.korting_type}
                    </span>
                  </td>
                  <td className="text-xs">
                    <div className="flex items-center gap-1">
                      {it.korting_type === 'CAT' && <Folder size={12} className="text-purple-500" />}
                      {it.korting_type === 'ART' && <Tag size={12} className="text-blue-500" />}
                      <span>{it.korting_label}</span>
                    </div>
                  </td>
                  <td className="right font-mono text-xs font-semibold">
                    {it.percentage > 0 ? `${it.percentage}%` : '—'}
                    {it.vast_bedrag > 0 && <div className="text-[10px] text-gray-400">+ {fmt(it.vast_bedrag)} vast</div>}
                  </td>
                  <td className="right font-mono text-xs">
                    {it.aantal_documenten > 0 ? (
                      <span className="px-1.5 py-0.5 bg-blue-50 text-blue-700 font-semibold">{it.aantal_documenten}</span>
                    ) : (
                      <span className="text-amber-600 font-semibold">0</span>
                    )}
                  </td>
                  <td className="right font-mono text-xs font-semibold">
                    {it.totaal_korting_eur > 0 ? fmt(it.totaal_korting_eur) : '—'}
                  </td>
                  <td className="text-[11px] text-gray-500 font-mono">
                    {it.laatst_gebruikt
                      ? new Date(it.laatst_gebruikt).toLocaleDateString('nl-BE', { day: '2-digit', month: '2-digit', year: '2-digit' })
                      : '—'}
                  </td>
                  <td>
                    <Link
                      to={`/klanten?id=${it.klant_id}&tab=kortingen`}
                      className="btn btn-ghost btn-sm"
                      title="Bekijk klant en kortingen"
                      data-testid={`audit-naar-klant-${idx}`}
                    >
                      <ArrowSquareOut size={12} />
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <p className="text-[10px] text-gray-400 mt-3">
        <strong>Matching:</strong> ART matcht regels via artikel-code; CAT via artikel-categorie; PG via klant.kortinggroep (huidige snapshot).
        Bij gemixte kortingen kan een regel onder meerdere rules vallen — totalen worden bij elke matching rule geteld.
      </p>
    </div>
  );
}
